import React, { useMemo, useState } from 'react';
import { Post } from '../../../../types/Post';
import { Comment } from '../../../../types';
import { CommentEntry, CommentInput, TextButton, ThreadLevel } from '../../..';
import { useInitialize, useToggle, useUser } from '../../../../hooks';
import { Column } from '../../../../styles';
import { AuthorContainer, CommentsButtonsGrid } from '../styles';
import {
    deletePostComment,
    fetchPostComments,
    postPostComment,
    updatePostComment
} from '../../../../api/comments';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { ProjectDetailsCoWorkLoadingComments } from './loading-comments/ProjectDetailsCoWorkLoadingComments';
import { DELETED_COMMENT } from '../../../../../constants';
import { CommentInfo } from '../../../comment/comment-info/CommentInfo';
import { useTranslations } from 'next-intl';

type Props = {
    post: Post;
    canCreatePost: boolean;
};

export const ProjectDetailsCoWorkPostCommentSection = ({ post, canCreatePost }: Props) => {
    const t = useTranslations('details');
    const { user } = useUser({ withProfilePicture: true });
    const [showCommentInput, toggleCommentInput] = useToggle();
    const [answersVisible, setAnswersVisible] = useState(0);
    const [isLoading, setIsLoading] = useToggle(true);
    const [comments, setComments] = useState<Comment[]>([]);

    const postId = post.id.toString();
    const showAnswers = answersVisible > 0;
    const canToggleMoreReplies = comments.length > answersVisible;

    const refreshComments = () =>
        fetchPostComments(postId).then((response) => {
            const newCommentsLength = response.comments.length;
            const hasNewComment = newCommentsLength - comments.length == 1;
            if (!canToggleMoreReplies && hasNewComment)
                setAnswersVisible((currrent) => currrent + 1);
            setComments(response.comments);
        });

    useInitialize(() => {
        refreshComments().finally(() => setIsLoading(false));
    });

    const visibleComments = useMemo(
        () => comments.slice(0, answersVisible),
        [comments, answersVisible]
    );

    if (!user || !post.author) return null;

    const onSubmitComment = (comment: string, replyTo?: string) =>
        postPostComment({
            entityId: postId,
            userId: user.id.toString(),
            threadId: replyTo ?? null,
            comment
        }).then(refreshComments);

    const onDeleteComment = (comment: Comment) =>
        // We do not want to delete whole threads, thats why we only mark
        //  deleted parent comments, but do not delete them actually
        comment.gotThread
            ? updatePostComment(postId, { ...comment, content: DELETED_COMMENT }).then(
                  refreshComments
              )
            : deletePostComment(postId, comment.id.toString(), comment.author.id.toString()).then(
                  refreshComments
              );

    const onUpdateComment = (updatedComment: Comment) =>
        updatePostComment(postId, updatedComment).then(refreshComments);

    return isLoading ? (
        <ProjectDetailsCoWorkLoadingComments />
    ) : (
        <Column>
            <AuthorContainer answersOpen={showAnswers} stretch>
                <CommentInfo
                    author={post.author}
                    createdAt={post.createdAt}
                    updatedAt={post.updatedAt}
                />

                {comments.length > 1 && (
                    <TextButton
                        icon={showAnswers ? <ExpandLess /> : <ExpandMore />}
                        label={
                            showAnswers
                                ? t('hide_replies', { count: comments.length })
                                : t('show_replies', { count: comments.length })
                        }
                        onClick={() => {
                            setAnswersVisible(showAnswers ? 0 : 10);
                            toggleCommentInput(false);
                        }}
                    />
                )}
                {canCreatePost && (
                    <TextButton
                        label={t('reply')}
                        onClick={() => {
                            toggleCommentInput(true);
                            if (!showAnswers) setAnswersVisible(10);
                        }}
                    />
                )}
            </AuthorContainer>
            <Column pl={5}>
                {showCommentInput && showAnswers && (
                    <CommentInput
                        author={post.author}
                        replyTo={post.author.name}
                        onSubmitComment={onSubmitComment}
                        mt={2}
                    />
                )}
                {showAnswers &&
                    visibleComments.map((c, i) => (
                        <CommentEntry
                            key={c.id}
                            comment={c}
                            threadLevel={ThreadLevel.First}
                            onReply={onSubmitComment}
                            isLastEntry={i === visibleComments.length - 1}
                            onDelete={onDeleteComment}
                            onUpdate={onUpdateComment}
                        />
                    ))}
            </Column>
            {showAnswers && (
                <CommentsButtonsGrid>
                    <div />
                    {comments.length > 1 && (
                        <TextButton
                            icon={<ExpandLess />}
                            label={t('hide_replies', { count: '' })}
                            onClick={() => setAnswersVisible(0)}
                            sx={{ width: 'fit-content', justifySelf: 'end' }}
                        />
                    )}
                    {canToggleMoreReplies && (
                        <TextButton
                            label={t('more_comments')}
                            onClick={() =>
                                setAnswersVisible((visibleAnswers) => visibleAnswers + 10)
                            }
                        />
                    )}
                </CommentsButtonsGrid>
            )}
        </Column>
    );
};
